// src/components/Navbar.tsx
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import UserMenu from './UserMenu';
import ThemeSwitcher from './ThemeSwitcher';
import { Zap, Merge, Split, Printer, Menu, X, Sparkles, BookOpen, Video } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const NotesCrafterLogo = () => (
  <img
    src="/logo.v2.png"
    alt="NotesCrafter Logo"
    className="h-10 w-auto"
    style={{ filter: 'drop-shadow(0 0 5px rgba(192, 132, 252, 0.4))' }}
  />
);

export default function Navbar() {
  const { isAuthenticated, isLoading, signIn } = useAuth();
  const { isDark } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');

  useEffect(() => {
    const handleNav = (e: any) => {
      if (e.detail?.page) setCurrentPage(e.detail.page);
    };
    window.addEventListener('navigateToPage', handleNav as EventListener);
    window.addEventListener('navigate', handleNav as EventListener);
    return () => {
      window.removeEventListener('navigateToPage', handleNav as EventListener);
      window.removeEventListener('navigate', handleNav as EventListener);
    };
  }, []);

  const handleNavigation = (destination: string, isPage = false) => {
    setIsMobileMenuOpen(false);
    if (isPage) {
      if (typeof window !== 'undefined' && window.scrollTo) {
        try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { window.scrollTo(0, 0); }
      }
      window.dispatchEvent(new CustomEvent('navigateToPage', { detail: { page: destination } }));
      window.dispatchEvent(new CustomEvent('navigate', { detail: { page: destination } }));
      setCurrentPage(destination);
      return;
    }
    // Navigate in same tab
    window.location.href = destination;
  };

  const navLinks = [
    { name: 'Home', label: 'Home', page: 'home', href: '/', current: currentPage === 'home' },
    { name: 'Flow', label: 'FLOW', href: 'https://flow.notescrafter.com' },
    { name: 'Prints', label: 'PRINTS', href: 'https://print.notescrafter.com' },
    { name: 'Tools', label: 'TOOLS', href: 'https://tools.notescrafter.com' },
  ];

  const quickLinks = [
    { name: 'Tutorial', href: 'https://youtube.com/@NotesCrafterApp', icon: Video },
    { name: 'How it works', page: 'how-to-print-online-lecture-notes', href: '/how-to-print-online-lecture-notes', icon: BookOpen },
    { name: 'Merge PDF', href: 'https://tools.notescrafter.com/tool/merge-pdf', icon: Merge },
    { name: 'Split PDF', href: 'https://tools.notescrafter.com/tool/split-pdf', icon: Split },
    { name: 'Print Notes', href: 'https://print.notescrafter.com', icon: Printer },
    { name: 'Upgrade Plan', page: 'pricing', href: '/pricing', icon: Zap },
  ];

  return (
    <div className="relative w-full z-50">
      <nav className="">
        {/* Desktop Navbar (lg:flex) */}
        <div className="hidden lg:flex w-full px-8 py-3 justify-between items-center">
          {/* Left: Logo */}
          <div
            className="flex items-center space-x-3 cursor-pointer group shrink-0"
            onClick={() => handleNavigation('home', true)}
          >
            <NotesCrafterLogo />
            <div className="flex flex-col leading-none">
              <span className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent group-hover:opacity-80 transition-opacity">
                NotesCrafter
              </span>
            </div>
          </div>

          {/* Right: Navigation Links, Action Buttons and Auth */}
          <div className="flex items-center space-x-6">
            {/* Desktop Navigation Links */}
            <div className="flex items-center space-x-8 mr-4">
              {navLinks.map((link) => {
                const isHome = link.name === 'Home';
                if (isHome) {
                  return (
                    <a
                      key={link.name}
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavigation('home', true);
                      }}
                      className={`text-sm font-semibold transition-colors duration-200 hover:scale-105 transform cursor-pointer ${
                        link.current ? 'text-primary font-black' : 'text-text-main hover:text-primary'
                      }`}
                    >
                      Home
                    </a>
                  );
                }

                return (
                  <a
                    key={link.name}
                    href={link.href}
                    className={`flex items-center gap-1 text-sm font-bold tracking-wider uppercase group/link transition-all duration-200 hover:scale-105 ${
                      link.current
                        ? 'text-primary font-black'
                        : 'text-text-main/80 hover:text-primary'
                    }`}
                  >
                    <span>{link.label}</span>
                  </a>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => handleNavigation('pricing', true)}
                className="glowing-glassy-pass group relative inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs sm:text-sm font-extrabold whitespace-nowrap overflow-hidden transition-all duration-300 hover:scale-105 active:scale-95 cursor-pointer"
              >
                {/* Shimmer sweep */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none" />
                <Sparkles className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 animate-pulse shrink-0" />
                <span className="relative tracking-wide">Get Community Pass</span>
              </button>
              <a
                href="https://play.google.com/store/apps/details?id=com.notescrafter.app"
                className="block hover:opacity-90 active:scale-95 transition-all duration-200 whitespace-nowrap shrink-0"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  className="h-[52px] w-auto object-contain select-none"
                />
              </a>
            </div>

            {/* Auth & Theme Switcher */}
            {!isLoading && (
              isAuthenticated ? (
                <div className="flex items-center gap-2.5">
                  <ThemeSwitcher />
                  <UserMenu />
                </div>
              ) : (
                <div className="flex items-center gap-2.5">
                  <ThemeSwitcher />
                  <button
                    onClick={signIn}
                    className="px-4 py-2 bg-gradient-to-r from-primary to-secondary text-white font-semibold rounded-full text-sm hover:shadow-lg hover:shadow-primary/30 transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    Sign In
                  </button>
                </div>
              )
            )}
          </div>
        </div>

        {/* Mobile & Tablet Navbar (lg:hidden) */}
        <div className="lg:hidden w-full px-4 sm:px-8 py-3 flex items-center justify-between relative">
          {/* Left End: 3-line hamburger menu button */}
          <div className="flex items-center justify-start w-12">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-text-main hover:text-primary transition-colors p-2 -ml-2 focus:outline-none cursor-pointer"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Center: Brand name & logo centered */}
          <div
            className="flex items-center space-x-2 cursor-pointer group"
            onClick={() => handleNavigation('home', true)}
          >
            <NotesCrafterLogo />
            <div className="flex flex-col leading-none text-left">
              <span className="text-base sm:text-lg font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent group-hover:opacity-80 transition-opacity">
                NotesCrafter
              </span>
            </div>
          </div>

          {/* Right End: Theme switcher + Avatar (UserMenu) or balanced spacer */}
          <div className="flex items-center justify-end gap-2.5 shrink-0">
            <ThemeSwitcher />
            {!isLoading && (
              isAuthenticated ? (
                <UserMenu />
              ) : (
                <div className="w-1" />
              )
            )}
          </div>
        </div>
      </nav>

      {/* Mobile/Tablet Menu Dropdown Panel */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="lg:hidden w-full absolute top-[100%] left-0 right-0 z-50 bg-white/95 dark:bg-[#0c0c14]/95 border-b border-black/10 dark:border-white/10 backdrop-blur-3xl shadow-2xl overflow-hidden flex flex-col"
          >
            <div className="p-5 flex flex-col gap-6 max-h-[80vh] overflow-y-auto">
              {/* Part 1: Quick Tools & Features */}
              <div className="flex flex-col gap-2.5">
                <span className="text-[10px] uppercase font-black tracking-widest text-purple-600 dark:text-purple-400 opacity-85 px-2">
                  Flow & Tools
                </span>
                <div className="grid grid-cols-2 gap-2">
                  {quickLinks.map((link) => {
                    const isInternal = Boolean(link.page);
                    return (
                      <a
                        key={link.name}
                        href={link.href}
                        onClick={(e) => {
                          if (isInternal) {
                            e.preventDefault();
                            handleNavigation(link.page!, true);
                          } else {
                            setIsMobileMenuOpen(false);
                          }
                        }}
                        className="flex items-center gap-2 px-3 py-3 rounded-xl border border-black/[0.08] dark:border-white/[0.08] bg-slate-50/90 dark:bg-white/[0.03] text-slate-800 dark:text-zinc-200 hover:text-purple-600 dark:hover:text-white hover:bg-purple-50 dark:hover:bg-white/[0.06] transition-all"
                      >
                        {link.icon && <link.icon size={16} className="text-purple-600 dark:text-purple-400 shrink-0" />}
                        <span className="font-semibold text-xs text-left truncate">{link.name}</span>
                      </a>
                    );
                  })}
                </div>
              </div>

              {/* Part 2: Global Ecosystem Services */}
              <div className="flex flex-col gap-2.5">
                <span className="text-[10px] uppercase font-black tracking-widest text-purple-600 dark:text-purple-400 opacity-85 px-2">
                  Ecosystem
                </span>
                <div className="flex flex-col gap-2">
                  {navLinks.map((link) => {
                    const isInternal = Boolean(link.page);
                    return (
                      <a
                        key={link.name}
                        href={link.href}
                        onClick={(e) => {
                          if (isInternal) {
                            e.preventDefault();
                            handleNavigation(link.page!, true);
                          } else {
                            setIsMobileMenuOpen(false);
                          }
                        }}
                        className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                          link.current
                            ? 'border-purple-500/40 bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 font-bold'
                            : 'border-black/[0.08] dark:border-white/[0.08] bg-slate-50/90 dark:bg-white/[0.03] text-slate-800 dark:text-zinc-200 hover:text-purple-600 dark:hover:text-white hover:bg-purple-50 dark:hover:bg-white/[0.06]'
                        }`}
                      >
                        <span className="text-sm font-semibold">{link.label || link.name}</span>
                      </a>
                    );
                  })}
                </div>
              </div>

              {/* Part 3: Support & Download Actions */}
              <div className="flex items-center gap-2 border-t border-black/5 dark:border-white/5 pt-4">
                <button
                  onClick={() => handleNavigation('pricing', true)}
                  className="glowing-glassy-pass flex-1 py-3 text-center rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Sparkles className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 animate-pulse shrink-0" />
                  <span>Get Pass</span>
                </button>
                <a
                  href="https://play.google.com/store/apps/details?id=com.notescrafter.app"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex-1 py-3 text-center bg-gradient-to-r from-primary to-secondary rounded-xl text-xs font-bold text-white hover:opacity-90 active:scale-95 transition-all shadow-md"
                >
                  Download App
                </a>
              </div>

              {/* Part 4: Guest Mobile Log In */}
              {!isLoading && !isAuthenticated && (
                <div className="border-t border-black/5 dark:border-white/5 pt-2">
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      signIn();
                    }}
                    className="w-full py-3.5 bg-gradient-to-r from-primary to-secondary text-white font-bold rounded-xl text-sm shadow-lg shadow-primary/20 active:scale-95 transition-all cursor-pointer"
                  >
                    Log In / Sign Up
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Sub-header (Secondary Nav) ── */}
      <div className="hidden lg:flex w-full px-8 justify-end">
        <div
          className="relative overflow-hidden transition-all duration-300 border-t border-l border-black/10 dark:border-white/10 rounded-tl-2xl bg-white/80 dark:bg-white/[0.03] backdrop-blur-md shadow-xs"
        >
          <div className="relative px-6 py-2 flex items-center gap-1 overflow-x-auto scrollbar-none">
            {quickLinks.map((link) => {
              const isUpgrade = link.name === 'Upgrade Plan';
              const isInternal = Boolean(link.page);

              const activeStyle = {
                color: isUpgrade
                  ? (isDark ? '#A855F7' : '#7C3AED')
                  : (isDark ? '#A39FC7' : '#475569'),
                background: isUpgrade
                  ? (isDark 
                      ? 'linear-gradient(180deg, rgba(168,85,247,0.18) 0%, rgba(99,102,241,0.10) 100%)' 
                      : 'linear-gradient(180deg, rgba(124,58,237,0.14) 0%, rgba(99,102,241,0.08) 100%)')
                  : (isDark ? 'rgba(168,85,247,0.06)' : 'rgba(124,58,237,0.04)'),
                clipPath: 'polygon(8px 0%, calc(100% - 8px) 0%, 100% 100%, 0% 100%)',
              };

              return (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => {
                    if (isInternal) {
                      e.preventDefault();
                      handleNavigation(link.page!, true);
                    }
                  }}
                  className="relative shrink-0 px-4 py-2 text-[10px] md:text-xs font-semibold transition-all duration-200 hover:text-primary flex items-center gap-1.5 cursor-pointer"
                  style={activeStyle}
                >
                  {link.icon && (
                    <link.icon
                      size={16}
                      className={`opacity-75 ${isUpgrade ? 'animate-pulse' : ''}`}
                    />
                  )}
                  {link.name}
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}